import { Skeleton } from '@shared/components/shadcn';
import type { LotteryName } from '../../lottery-purchases/types';
import { LIVE_DRAW_BALL_SHAPE_CLASSES, LIVE_DRAW_EXTRAS_DIVIDER_CLASSES, LIVE_DRAW_EXTRAS_ROW_CLASSES, LIVE_DRAW_GRID_CLASSES } from '../components/LiveDrawPanel/liveDrawClasses';
import { getLiveDrawExtraNumberPool, getLiveDrawNumberPool } from '../liveDraw/numberPool';
import { LIVE_DRAW_SKELETON_FILL, LiveDrawCardSkeleton } from './LiveDrawCardSkeleton';

type LiveDrawGridSkeletonProps = {
  lotteryName: LotteryName;
};

const BALL_CLASSES = `${LIVE_DRAW_BALL_SHAPE_CLASSES} ${LIVE_DRAW_SKELETON_FILL}`;

/**
 * Holds the live-draw board's box while the draw is still loading.
 *
 * Ball count follows {@link getLiveDrawNumberPool} and the spacing comes from the classes the board
 * itself uses, the same sources as `DrawnNumbersGrid`. That way Daily Million's 7×7, and a lottery
 * with an extras row (and the labelled divider above it), keep the height the loaded card will take.
 */
function LiveDrawGridSkeleton({ lotteryName }: LiveDrawGridSkeletonProps) {
  const pool = getLiveDrawNumberPool(lotteryName);
  const extraPool = getLiveDrawExtraNumberPool(lotteryName);

  return (
    <LiveDrawCardSkeleton data-testid="live-draw-grid-skeleton">
      <div className="flex w-full flex-col items-center">
        <div className={LIVE_DRAW_GRID_CLASSES}>
          {pool.map(number => (
            <Skeleton key={number} data-testid="live-draw-grid-skeleton-ball" className={BALL_CLASSES} />
          ))}
        </div>
        {extraPool.length > 0 ? (
          <>
            <div data-testid="live-draw-grid-skeleton-divider" className={LIVE_DRAW_EXTRAS_DIVIDER_CLASSES.root}>
              <span className={LIVE_DRAW_EXTRAS_DIVIDER_CLASSES.rule} aria-hidden />
              <span className={LIVE_DRAW_EXTRAS_DIVIDER_CLASSES.pill}>
                {/* One line of the divider's `paragraph-s` label; the sunbursts beside it are shorter. */}
                <Skeleton className={`paragraph-s h-lh w-24 rounded-full ${LIVE_DRAW_SKELETON_FILL}`} />
              </span>
              <span className={LIVE_DRAW_EXTRAS_DIVIDER_CLASSES.rule} aria-hidden />
            </div>
            <div data-testid="live-draw-grid-skeleton-extras" className={LIVE_DRAW_EXTRAS_ROW_CLASSES}>
              {extraPool.map(number => (
                <Skeleton key={number} className={BALL_CLASSES} />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </LiveDrawCardSkeleton>
  );
}

export { LiveDrawGridSkeleton, type LiveDrawGridSkeletonProps };
